// YNX — Jitsi meeting room. Uses the External API (free meet.jit.si infra).
// The local user is automatically promoted to moderator (first to join = host).
// We expose host controls: mute everyone, toggle lobby, kick, share, …
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  MicOff, X, Share2, Copy, Hand, ShieldCheck,
} from 'lucide-react';

const JITSI_SCRIPT = (host) => `https://${host || 'meet.jit.si'}/external_api.js`;

let _loaderPromise = null;
const loadJitsi = (host) => {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = JITSI_SCRIPT(host);
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { _loaderPromise = null; reject(new Error('Jitsi script load failed')); };
    document.head.appendChild(s);
  });
  return _loaderPromise;
};

export const JitsiRoom = ({ meeting, displayName, email, onClose }) => {
  const wrapperRef = useRef(null);
  const apiRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState([]);     // [{id,name}]
  const [copied, setCopied] = useState(false);

  const host = meeting?.jitsi_host || 'meet.jit.si';
  const roomName = meeting?.room_name;
  const joinUrl = meeting?.join_url || `https://${host}/${roomName}`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadJitsi(host);
        if (!mounted || !wrapperRef.current) return;
        // Clean previous instance
        if (apiRef.current) { apiRef.current.dispose(); apiRef.current = null; }
        // Safe DOM clearing — never assign innerHTML to user-controlled content
        while (wrapperRef.current.firstChild) {
          wrapperRef.current.removeChild(wrapperRef.current.firstChild);
        }

        const api = new window.JitsiMeetExternalAPI(host, {
          roomName,
          parentNode: wrapperRef.current,
          userInfo: {
            displayName: displayName || 'Hôte YNX',
            email: email || '',
          },
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            requireDisplayName: false,
            enableWelcomePage: false,
            enableClosePage: false,
          },
          interfaceConfigOverwrite: {
            DEFAULT_BACKGROUND: '#020509',
            DEFAULT_LOGO_URL: '',
            DEFAULT_WELCOME_PAGE_LOGO_URL: '',
            JITSI_WATERMARK_LINK: '',
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            BRAND_WATERMARK_LINK: '',
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'profile', 'chat',
              'raisehand', 'videoquality', 'tileview', 'select-background',
              'mute-everyone', 'mute-video-everyone', 'security', 'settings',
            ],
          },
        });
        apiRef.current = api;

        // Try to set the meeting password (best-effort)
        if (meeting?.require_password && meeting?.password) {
          api.addListener('participantRoleChanged', (e) => {
            if (e.role === 'moderator') {
              try { api.executeCommand('password', meeting.password); }
              catch (err) { console.warn('YNX Jitsi: set password failed', err); }
            }
          });
        }

        api.addListener('videoConferenceJoined', () => setReady(true));
        api.addListener('readyToClose', () => onClose?.());
        api.addListener('participantJoined', () => {
          try {
            const list = api.getParticipantsInfo?.() || [];
            setParticipants(list.map((p) => ({ id: p.participantId, name: p.displayName })));
          } catch (err) { console.debug('YNX Jitsi: participantsInfo join failed', err); }
        });
        api.addListener('participantLeft', () => {
          try {
            const list = api.getParticipantsInfo?.() || [];
            setParticipants(list.map((p) => ({ id: p.participantId, name: p.displayName })));
          } catch (err) { console.debug('YNX Jitsi: participantsInfo leave failed', err); }
        });
      } catch (e) {
        setError(e?.message || 'Impossible de charger le studio Jitsi.');
      }
    })();
    return () => {
      mounted = false;
      try { apiRef.current?.dispose(); }
      catch (err) { console.debug('YNX Jitsi: dispose failed', err); }
      apiRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName, host]);

  const muteEveryone = useCallback(() => {
    try { apiRef.current?.executeCommand('muteEveryone'); }
    catch (err) { console.warn('YNX Jitsi: muteEveryone failed', err); }
  }, []);

  const muteEveryoneVideo = useCallback(() => {
    try { apiRef.current?.executeCommand('muteEveryoneVideo'); }
    catch (err) { console.warn('YNX Jitsi: muteEveryoneVideo failed', err); }
  }, []);

  const toggleLobby = useCallback(() => {
    try { apiRef.current?.executeCommand('toggleLobby', true); }
    catch (err) { console.warn('YNX Jitsi: toggleLobby failed', err); }
  }, []);

  const hangUp = useCallback(() => {
    try { apiRef.current?.executeCommand('hangup'); }
    catch (err) { console.warn('YNX Jitsi: hangup failed', err); }
    onClose?.();
  }, [onClose]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) { console.warn('YNX Jitsi: clipboard copy failed', err); }
  };

  const share = async () => {
    const text = `Rejoignez ma réunion YNX : ${meeting?.title || ''}\n${joinUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: meeting?.title || 'Réunion YNX', text, url: joinUrl }); return; }
      catch (err) { console.debug('YNX Jitsi: navigator.share aborted', err); }
    }
    copyLink();
  };

  return (
    <div className='jitsi-overlay' role='dialog' aria-label='Réunion vidéo YNX' data-testid='jitsi-overlay'>
      <header className='jitsi-bar'>
        <div className='jitsi-bar-title'>
          <ShieldCheck size={14} /> {meeting?.title || 'Réunion YNX'}
          <span className='jitsi-bar-room'>· {roomName}</span>
        </div>
        <div className='jitsi-bar-actions'>
          <button className='jitsi-btn' onClick={copyLink} title='Copier le lien' data-testid='jitsi-copy-btn'>
            <Copy size={13} /> {copied ? 'Copié !' : 'Copier'}
          </button>
          <button className='jitsi-btn' onClick={share} title='Partager' data-testid='jitsi-share-btn'>
            <Share2 size={13} /> Partager
          </button>
          <button className='jitsi-btn host' onClick={muteEveryone} title='Mettre tout le monde en sourdine' data-testid='jitsi-mute-all-btn'>
            <MicOff size={13} /> Muter tout
          </button>
          <button className='jitsi-btn host' onClick={muteEveryoneVideo} title='Couper toutes les caméras'>
            Caméras off
          </button>
          <button className='jitsi-btn host' onClick={toggleLobby} title='Activer la salle d’attente'>
            <Hand size={13} /> Lobby
          </button>
          <button className='jitsi-btn danger' onClick={hangUp} title='Raccrocher et fermer' data-testid='jitsi-hangup-btn'>
            <X size={14} /> Quitter
          </button>
        </div>
      </header>
      <div className='jitsi-stage' ref={wrapperRef} />
      {!ready && !error && <div className='jitsi-loading'>Connexion au studio Jitsi…</div>}
      {error && <div className='jitsi-error'>{error}</div>}
      {participants.length > 1 && (
        <div className='jitsi-participants'>
          👥 {participants.length} participants
        </div>
      )}
    </div>
  );
};

export default JitsiRoom;
